// Canonical team-name normalization.
//
// A team's slug is the join key used everywhere: it keys the AuthContext teams
// map, it is what gets written into weekly_lineups starters/bench arrays, and
// it names the logo file in /public/logos.
//
// Ampersands become "-", not "". This matches teams.id in Supabase
// ("Texas A&M" -> "texas-a-m") and was already the behaviour of most call
// sites. Do not "simplify" it to strip the ampersand — that produces
// "texas-am", which silently fails to match stored roster data.
//
// Import this. Do not hand-roll the regex; divergent copies previously caused
// Texas A&M to miss every teams-map lookup.
export const normalizeTeamName = (name) =>
  name
    ?.toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/&/g, "-")
    .replace(/[^a-z0-9-]/g, "");

// The inverse, for display only: "texas-a-m" -> "Texas A&M". Lossy by nature,
// a slug has already lost its casing and punctuation, so it is a fallback for
// a slug the teams map does not know. FCS opponents are in `teams` (and so in
// the map) same as FBS; this now only fires for something genuinely
// unmapped, e.g. a D2 opponent or a new FCS team not yet backfilled. Never
// feed the result back into a lookup.
export const denormalizeTeamName = (slug) => {
  if (!slug) return slug;
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
    .replace(/\bAnd\b/g, "&");
};

// Display name for a slug out of `games.home_team` / `games.away_team`, which
// store slugs rather than school names. Prefers the real school name from the
// AuthContext teams map and falls back to de-slugging.
export const teamLabel = (slug, teamsMap) =>
  slug ? teamsMap?.[slug]?.school || denormalizeTeamName(slug) : "";

// Combining diacritical marks left behind by an NFD decomposition.
const COMBINING_MARKS = /[̀-ͯ]/g;

// Strips accents so "San José State" folds to "san-jose-state" rather than
// losing the character entirely ("san-jos-state").
export const foldAccents = (name) =>
  name?.normalize("NFD").replace(COMBINING_MARKS, "");

// Fallback for a school with no /public/logos file: every FCS and D2 team,
// none of which have a logo checked in, whether or not they are in `teams`.
// Was only implemented once (in TeamLogoImage) and every other logo circle
// just went blank on a 404; shared here so every logo spot degrades the same
// way.
export const getTeamInitials = (name) => {
  if (!name) return "?";
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return name.slice(0, 2).toUpperCase();
  return words.map((w) => w[0]).join("").slice(0, 2).toUpperCase();
};
